import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ordersService } from '../../services/orders.service';
import { MessageService } from 'primeng/api';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

export type TripRole = 'ROLE_KAM' | 'ROLE_PROCUREMENT' | 'ROLE_PROCUREMENT_HEAD' | 'ROLE_VENDOR' | 'ROLE_OPERATION';

// ── Order (from getOrderById) ──
export interface TripOrderViewModel {
  id: number;
  orderNo: string;
  customerName: string;
  orderType: string;
  vehicleRequiredAt: string;
  route: string;
  source: string;
  destination: string;
  vehicleTypeId: number;
  numberOfVehicle: number;
  vendorId?: number;
  accountId?: number;
  customerId?: any;
  orderId: number
  status: string;
  confirmedAmount?: number;
  orderStatus?: any[];
}

// ── Trip (from getOrderTripById) ──
export interface TripDataModel {
  id?: number;
  orderId?: number;
  accountId?: number;
  customerId?: number;
  vendorId?: number;
  vendorName?: string;
  tripNumber?: string;
  vehicleNumber?: string;
  driverName?: string;
  driverMobile?: string;
  vendorMobile?: any;
  licenseNumber?: string;
  sealNumber?: string;
  advanceAmount?: number;
  advancePercentage:any;
  reportingDateTime?: string;
  loadingDateTime?: string;
  materialTypeDescription?: string;
  weight?: number;
  numberOfPackages?: number;
  managementStatus?: string;
  lhcStatus?: string;
  status?: string;
}

// ── One row in the repeatable LHC form (vehicle + payment only) ──
export interface LhcFormRow {
  id?: number;          // trip id once saved, undefined for a new unsaved row
  vehicleNumber: string;
  driverName: string;
  driverMobile: string;
  vendorMobile:any;
  vendorName:any;
  licenseNumber: string;
  advanceAmount: number | null;
  advancePercentage:any;
  saving?: boolean;
}

// ── One row in the repeatable LR — Lorry Receipt section ──
// Decoupled from LhcFormRow — count of LRs is independent of vehicle count.
// All rows are submitted together via a single section-level Save.
export interface LrFormRow {
  id?: number;
  isManual: boolean;        // true = Manual Entry, false = Auto Generate
  lrNumber: string;
  lrDate: string;            // datetime-local bound value
  remarks: string;
  lrDocument?: string;       // URL returned by the upload API
  uploading?: boolean;
}

// ── One row in the repeatable E-Way Bill section ──
// All rows are submitted together via a single section-level Save.
export interface EwayBillRow {
  id?: number;

  gstNumber: string;
  ewayBillNumber: string;
  fetching?: boolean;
  fetched?: boolean;
  uploading?: boolean;
  docUrl?: string;

  // Display fields populated after Fetch
  ewbId?: number;
  invoiceNo?: string;
  invoiceValue?: number;
  vehicleNo?: string;
  lrNumber?: string;

  // Raw "DD-MM-YYYY HH:mm:ss +0000" strings exactly as returned by the
  // fetch API — passed straight through to the save payload unchanged.
  rawInvoiceDate?: string;
  rawLrDate?: string;
  rawEwbDate?: string;
  rawValidUpto?: string;

  // Parsed Date objects — used only for display formatting.
  invoiceDateObj?: Date | null;
  lrDateObj?: Date | null;
  ewbDateObj?: Date | null;
  validUptoObj?: Date | null;
}

@Component({
  selector: 'app-trip-order',
  templateUrl: './trip-order.component.html',
  styleUrls: ['./trip-order.component.css']
})
export class TripOrderComponent implements OnInit {

  role: TripRole = 'ROLE_PROCUREMENT';
  userName = '';
  orderId: any;

  order!: TripOrderViewModel;
  tripData: TripDataModel | null = null;

  isLoading = false;
  loadingSaving = false;
  lrSaving = false;
  ewaySaving = false;

  // Repeatable LHC rows — vehicle/driver/payment only
  lhcRows: LhcFormRow[] = [];

  // Repeatable LR — Lorry Receipt rows — independent of LHC rows.
  // Single Save button submits every row.
  lrRows: LrFormRow[] = [];

  // Repeatable E-Way Bill rows — single Save button submits every row.
  ewayRows: EwayBillRow[] = [];

  // GST numbers available for this account — populated for the
  // e-Way Bill "GST Number" dropdown.
  gstList: any[] = [];

  loadingForm = {
    reportingDateTime: '',
    loadingDateTime: '',
    sealNumber: '',
    materialTypeDescription: '',
    weight: null as number | null,
    numberOfPackages: null as number | null,
  };

  ewayBillList:any;
  lrInfoList:any;

  private readonly uploadApi     = 'https://ftl-dev.apollosupplychain.com/utils-service/uploads/uploadFiles';

  constructor(
    private router: Router,
    private activeRoute: ActivatedRoute,
    private orderService: ordersService,
    private messageService: MessageService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    const userDetails = JSON.parse(localStorage.getItem('UserDetails') || '{}');
    this.role = userDetails?.roleName || 'ROLE_PROCUREMENT';
    this.userName = userDetails?.name || '';

    this.orderId = this.activeRoute.snapshot.params['id'];
    if (this.orderId) {
      this.isLoading = true;
      this.getOrderDatabyId(this.orderId);
    }
  }

  // ─────────────────────────────────────────────
  // DATA LOADING
  // ─────────────────────────────────────────────

  // Load order first — its orderStatus tells us whether a trip
  // record already exists (TRIP_STATUS !== 'PENDING') or whether
  // we need to create one before loading trip details.
  getOrderDatabyId(orderId: any): void {
    this.orderService.getOrderById(orderId).subscribe({
      next: (res: any) => {
        const data = res?.responseObject;
        if (data) {
          this.mapResponseToOrder(data);
          this.loadGstList();

          if (this.isTripPending()) {
            this.createTrip();
          } else {
            this.getOrderTripById(this.orderId);
          }
        } else {
          this.isLoading = false;
        }
      },
      error: () => { this.isLoading = false; }
    });
  }

  isTripPending(): boolean {
    return this.order?.orderStatus?.some(
      (el: any) => el.statusType === 'TRIP_STATUS' && el.status === 'PENDING'
    ) || false;
  }

  // First-time only: create the trip shell record, then load
  // its details the normal way.
  createTrip(): void {
    const payload: any = {
      orderId:    this.order.id,
      accountId:  this.order.accountId || 1,
      customerId: this.order.id,
      status:     'TRIP_GENERATED',
      vendorId:   this.order.vendorId,
    };

    this.orderService.saveTrip(payload).subscribe({
      next: () => {
        this.getOrderTripById(this.orderId);
      },
      error: (err: any) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to create trip' });
      }
    });
  }

  getOrderTripById(orderId: any): void {
    this.orderService.getOrderTripById(orderId).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        const data = res?.responseObject;
        if (data) {
          this.mapResponseToTrip(data);
          this.getEwayBillList(data?.id)
          this.getLrInfoList(data?.id)
        } else {
          this.ensureAtLeastOneLhcRow();
          this.ensureAtLeastOneLrRow();
          this.ensureAtLeastOneEwayRow();
        }
      },
      error: () => {
        this.isLoading = false;
        this.ensureAtLeastOneLhcRow();
        this.ensureAtLeastOneLrRow();
        this.ensureAtLeastOneEwayRow();
      }
    });
  }

  getEwayBillList(id: any) {

    const filter = [
      { key: 'TRIP_ID_IN', value: id }
    ];
  
    this.orderService.getEwayBillList(filter, 0, 100000).subscribe((res: any) => {
  
      this.ewayBillList = res?.responseObject?.content || [];
  
      if (this.ewayBillList.length) {
  
        this.ewayRows = this.ewayBillList.map((item: any) => ({
          id: item.id,

          gstNumber: item.gstNo,
          ewayBillNumber: item.ewbNo,
  
          fetched: true,
  
          invoiceNo: item.invoiceNo,
          invoiceValue: item.invValue,
          vehicleNo: item.vehicleNo,
          lrNumber: item.lrNo,
  
          rawInvoiceDate: item.invoiceDate,
          rawLrDate: item.lrDate,
          rawEwbDate: item.ewbDate,
          rawValidUpto: item.validUpto,
  
          invoiceDateObj: this.parseApiDateString(item.invoiceDate),
          lrDateObj: this.parseApiDateString(item.lrDate),
          ewbDateObj: this.parseApiDateString(item.ewbDate),
          validUptoObj: this.parseApiDateString(item.validUpto),
  
          docUrl: item.docUrl
        }));
  
      } else {
        this.ensureAtLeastOneEwayRow();
      }
  
    });
  }
  getLrInfoList(id: any) {
    const filter = [
      { key: 'TRIP_ID_IN', value: id }
    ];
  
    this.orderService.getLrInfoList(filter, 0, 100000).subscribe((res: any) => {
  
      this.lrInfoList = res?.responseObject?.content || [];
  
      if (this.lrInfoList.length) {
  
        this.lrRows = this.lrInfoList.map((item: any) => ({
          id: item.id,
          isManual: item.isManual,
          lrNumber: item.lrNumber || '',
          lrDate: this.toLocalDatetime(item.lrDate),
          remarks: item.remarks || '',
          lrDocument: item.lrDocument || item.docUrl || ''
        }));
  
      } else {
        this.ensureAtLeastOneLrRow();
      }
  
    });
  }

  // GST numbers for this account — feeds the e-Way Bill "GST Number" dropdown.
  loadGstList(): void {
    const accountId = this.order?.accountId || 1;
    this.orderService.getGstList(accountId).subscribe({
      next: (res: any) => {
        this.gstList = res?.responseObject || res?.data || [];
      },
      error: () => { this.gstList = []; }
    });
  }

  mapResponseToOrder(data: any): void {
    const pickup = data.orderPoints?.find((p: any) => p.pointType === 'PICKUP');
    const drop   = data.orderPoints?.find((p: any) => p.pointType === 'DROP');
    const route  = (data.source && data.destination)
      ? `${data.source} - ${data.destination}`
      : (pickup && drop)
        ? `${pickup.address} - ${drop.address}`
        : '';

    this.order = {
      id:                data.id,
      orderNo:           data.orderNumber,
      customerId:        data?.customerId,
      orderId:            data.orderId,
      customerName:      data.customerName || '',
      orderType:         data.orderType || 'spot',
      vehicleRequiredAt: data.vehicleRequiredAt || '',
      route,
      source:            data.source || pickup?.address || '',
      destination:       data.destination || drop?.address || '',
      vehicleTypeId:     data.vehicleTypeId,
      numberOfVehicle:   data.numberOfVehicle || 1,
      vendorId:          data.vendorId,
      accountId:         data.accountId,
      status:            data.status || '',
      confirmedAmount:   data.confirmedAmount,
      orderStatus:       data.orderStatus || [],
    };
  }

  
  checkTripStatus(status:any): boolean {
    return this.order?.orderStatus?.some(
      (el: any) =>
        el.statusType === 'TRIP_STATUS' &&
        el.status === status
    ) || false;
  }

  // Trip API returns a single flat trip object — seed the first
  // LHC row and the loading-info form from it. LR / E-Way Bill
  // rows are independent forms the user builds up locally.
  mapResponseToTrip(trip: TripDataModel): void {
    this.tripData = trip;

    this.lhcRows = [{
      id:            trip.id,
      vehicleNumber: trip.vehicleNumber  || '',
      driverName:    trip.driverName     || '',
      driverMobile:  trip.driverMobile   || '',
      vendorMobile:  trip.vendorMobile   || '',
      vendorName:    trip.vendorName     || '',
      licenseNumber: trip.licenseNumber  || '',
      advanceAmount: trip.advanceAmount  ?? null,
      advancePercentage: trip.advancePercentage ?? null
    }];

    this.loadingForm = {
      reportingDateTime:       this.toLocalDatetime(trip.reportingDateTime),
      loadingDateTime:         this.toLocalDatetime(trip.loadingDateTime),
      sealNumber:              trip.sealNumber              || '',
      materialTypeDescription: trip.materialTypeDescription || '',
      weight:                  trip.weight          ?? null,
      numberOfPackages:        trip.numberOfPackages ?? null,
    };

    // this.ensureAtLeastOneLrRow();
    // this.ensureAtLeastOneEwayRow();
  }

  ensureAtLeastOneLhcRow(): void {
    if (!this.lhcRows.length) {
      this.lhcRows = [this.emptyLhcRow()];
    }
  }

  ensureAtLeastOneLrRow(): void {
    if (!this.lrRows.length) {
      this.lrRows = [this.emptyLrRow()];
    }
  }

  ensureAtLeastOneEwayRow(): void {
    if (!this.ewayRows.length) {
      this.ewayRows = [this.emptyEwayRow()];
    }
  }

  emptyLhcRow(): LhcFormRow {
    return {
      vehicleNumber: '',
      driverName: '',
      driverMobile: '',
      vendorMobile: '',
      vendorName: '',
      licenseNumber: '',
      advanceAmount: null,
      advancePercentage: null
    };
  }

  emptyLrRow(): LrFormRow {
    return {
      isManual: true,
      lrNumber: '',
      lrDate: '',
      remarks: '',
    };
  }

  emptyEwayRow(): EwayBillRow {
    return {
      gstNumber: '',
      ewayBillNumber: '',
    };
  }

  // ─────────────────────────────────────────────
  // ROLE GATES
  // ─────────────────────────────────────────────

  canEditLhc(): boolean {
    return this.role === 'ROLE_PROCUREMENT';
  }

  canEditLoadingInfo(): boolean {
    return this.role === 'ROLE_OPERATION';
  }

  canEditEwayBill(): boolean {
    return this.role === 'ROLE_OPERATION';
  }

  // ─────────────────────────────────────────────
  // LHC — repeatable rows (ROLE_PROCUREMENT) — unchanged, per-row save
  // ─────────────────────────────────────────────

  addLhcRow(): void {
    this.lhcRows.push(this.emptyLhcRow());
  }

  removeLhcRow(index: number): void {
    if (this.lhcRows.length > 1) {
      this.lhcRows.splice(index, 1);
    }
  }

  // isLhcRowValid(row: LhcFormRow): boolean {
  //   // return !!(row.vehicleNumber && row.driverName && row.driverMobile);
  //   return !!(row.advanceAmount);
  // }
  isLhcRowValid(row: LhcFormRow): boolean {
    const totalFreight = Number(this.order?.confirmedAmount || 0);
  
    return !!row.advanceAmount &&
           row.advanceAmount > 0 &&
           row.advanceAmount <= totalFreight;
  }

  saveLhcRow(row: LhcFormRow): void {
    if (!this.isLhcRowValid(row)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Required',
        detail: 'Advance Amount is required'
      });
      return;
    }

    const payload: any = {
      ...(row.id ? { id: row.id } : {}),
      orderId:          this.order.id,
      accountId:        this.tripData?.accountId || this.order.accountId || 1,
      customerId:       this.tripData?.customerId || this.order.id,
      vehicleNumber:    row.vehicleNumber,
      driverName:       row.driverName,
      driverMobile:     row.driverMobile,
      vendorMobile:     row.vendorMobile,
      vendorName:       row.vendorName,
      licenseNumber:    row.licenseNumber || undefined,
      advanceAmount:    row.advanceAmount?.toString() || '0',
      lhcStatus:        'CREATED',
      managementStatus: 'PENDING',
      status:           'TRIP_GENERATED',
    };

    row.saving = true;
    this.orderService.saveTrip(payload).subscribe({
      next: (res: any) => {
        row.saving = false;
        row.id = res?.responseObject?.id || row.id;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'LHC details saved successfully' });
        this.getOrderTripById(this.orderId);
      },
      error: (err: any) => {
        row.saving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to save LHC details' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // LR — Lorry Receipt — repeatable rows, single Save for all rows
  // ─────────────────────────────────────────────

  addLrRow(): void {
    this.lrRows.push(this.emptyLrRow());
  }

  removeLrRow(index: number): void {

    const row = this.lrRows[index];
    // Existing row
    if (row.id) {
      // this.orderService.deleteLr(row.id).subscribe({
      //   next: () => {
  
      //     this.lrRows.splice(index, 1);
  
      //     if (!this.lrRows.length) {
      //       this.lrRows.push(this.emptyLrRow());
      //     }
  
      //     this.messageService.add({
      //       severity: 'success',
      //       summary: 'Deleted',
      //       detail: 'LR deleted successfully'
      //     });
  
      //   },
      //   error: (err: any) => {
      //     this.messageService.add({
      //       severity: 'error',
      //       summary: 'Error',
      //       detail: err?.error?.responseMessage || 'Unable to delete LR'
      //     });
      //   }
      // });
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Unable to delete LR'
      })
    }
  
    else {
      this.lrRows.splice(index, 1);
      if (!this.lrRows.length) {
        this.lrRows.push(this.emptyLrRow());
      }
    }
  }

  // Random alphanumeric code used when "Auto Generate" is selected.
  generateLrNumber(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let out = '';
    for (let i = 0; i < 10; i++) {
      out += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return out;
  }

  onLrTypeChange(row: LrFormRow): void {
    if (!row.isManual) {
      row.lrNumber = this.generateLrNumber();
    } else {
      row.lrNumber = '';
    }
  }

  onLrFileSelected(event: any, row: LrFormRow): void {
    const file: File = event?.target?.files?.[0];
    if (!file) return;

    row.uploading = true;
    this.uploadFile(file).subscribe({
      next: (url) => {
        row.uploading = false;
        row.lrDocument = url;
      },
      error: () => {
        row.uploading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to upload LR document' });
      }
    });
  }

  isLrRowValid(row: LrFormRow): boolean {
    return !!(row.lrNumber && row.lrDate);
  }

  // Single Save for the whole LR section — fires one addOrUpdate
  // call per row, in parallel, and reports success only once all
  // rows have saved.
  saveAllLr(): void {
    const invalidRow = this.lrRows.find(r => !this.isLrRowValid(r));
    if (invalidRow) {
      this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'LR number & LR date are required for every row' });
      return;
    }

    const newRows = this.lrRows.filter(x => !x.id);
    if (!newRows.length) {
      this.messageService.add({
        severity: 'info',
        summary: 'No Changes',
        detail: 'There are no new LR records to save.'
      });
      return;
    }
    const requests = newRows.map(row => {
      const payload: any = {
        tripId:      this.tripData?.id,
        isManual:    row.isManual,
        lrNumber:    row.lrNumber,
        lrDate:      this.toApiDatetime(row.lrDate),
        lrDocument:  row.lrDocument || undefined,
        remarks:     row.remarks || undefined,
      };
      return this.orderService.saveAllLr(payload);
    });

    this.lrSaving = true;
    forkJoin(requests).subscribe({
      next: () => {
        this.lrSaving = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'LR details saved successfully' });
        this.getOrderTripById(this.orderId);
      },
      error: (err: any) => {
        this.lrSaving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to save LR details' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // Loading Info (ROLE_OPERATION)
  // ─────────────────────────────────────────────

  saveLoadingInfo(): void {
    const payload: any = {
      ...(this.tripData?.id ? { id: this.tripData.id } : {}),
      orderId:                 this.order.id,
      accountId:               this.tripData?.accountId || this.order.accountId || 1,
      customerId:              this.tripData?.customerId || this.order.id,
      reportingDateTime:       this.toApiDatetime(this.loadingForm.reportingDateTime),
      loadingDateTime:         this.toApiDatetime(this.loadingForm.loadingDateTime),
      sealNumber:              this.loadingForm.sealNumber              || undefined,
      materialTypeDescription: this.loadingForm.materialTypeDescription || undefined,
      weight:                  this.loadingForm.weight                  ?? undefined,
      numberOfPackages:        this.loadingForm.numberOfPackages        ?? undefined,
    };

    this.loadingSaving = true;
    this.orderService.saveTrip(payload).subscribe({
      next: () => {
        this.loadingSaving = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Loading info saved successfully' });
        this.getOrderTripById(this.orderId);
      },
      error: (err: any) => {
        this.loadingSaving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to save loading info' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // E-Way Bill — repeatable rows, single Save for all rows
  // ─────────────────────────────────────────────

  addEwayRow(): void {
    this.ewayRows.push(this.emptyEwayRow());
  }

  removeEwayRow(index: number): void {
    const row = this.ewayRows[index];
    if (row.id) {
      
      // this.orderService.deleteEwayBill(row.id).subscribe({
      //   next: () => {
  
      //     this.ewayRows.splice(index, 1);
  
      //     if (!this.ewayRows.length) {
      //       this.ewayRows.push(this.emptyEwayRow());
      //     }
  
      //     this.messageService.add({
      //       severity: 'success',
      //       summary: 'Deleted',
      //       detail: 'E-Way Bill deleted successfully'
      //     });
  
      //   },
      //   error: (err: any) => {
      //     this.messageService.add({
      //       severity: 'error',
      //       summary: 'Error',
      //       detail: err?.error?.responseMessage || 'Unable to delete E-Way Bill'
      //     });
      //   }
      // });
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Unable to delete E-Way Bill'
      })
  
    } else {
      this.ewayRows.splice(index, 1);
      if (!this.ewayRows.length) {
        this.ewayRows.push(this.emptyEwayRow());
      }
    }  
  }

  gstValue(g: any): string {
    return g?.gstNumber || g?.gstNo || g?.gstin || (typeof g === 'string' ? g : '');
  }

  gstLabel(g: any): string {
    const num = this.gstValue(g);
    const name = g?.tradeName || g?.legalName || g?.name;
    return name ? `${num} — ${name}` : num;
  }

  // Pulls invoice / vehicle / validity details for one e-Way Bill row
  // from the e-Way bill lookup. Date fields come back pre-formatted
  // as "DD-MM-YYYY HH:mm:ss +0000" — the same format the save API
  // expects, so they're stored as-is and only parsed separately for
  // on-screen display.
  fetchEwayBill(row: EwayBillRow): void {
    if (!row.gstNumber || !row.ewayBillNumber) {
      this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Select a GST number and enter the e-Way bill number' });
      return;
    }
    row.fetching = true;
    this.orderService.fetchEwayBill(row.ewayBillNumber,row.gstNumber).subscribe({
      next: (res: any) => {
        row.fetching = false;
        row.fetched = true;
        const data = res?.responseObject || {};

        row.ewbId        = data.ewbId;
        row.invoiceNo    = data.invoiceNo;
        row.invoiceValue = data.invValue;
        row.vehicleNo    = data.vehicleNo;
        row.lrNumber     = data.lrNo;

        row.rawInvoiceDate = data.invoiceDate;
        row.rawLrDate      = data.lrDate;
        row.rawEwbDate     = data.ewbDate;
        row.rawValidUpto   = data.validUpto;

        row.invoiceDateObj = this.parseApiDateString(data.invoiceDate);
        row.lrDateObj      = this.parseApiDateString(data.lrDate);
        row.ewbDateObj      = this.parseApiDateString(data.ewbDate);
        row.validUptoObj   = this.parseApiDateString(data.validUpto);
      },
      error: (err: any) => {
        row.fetching = false;
        row.fetched = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to fetch e-Way bill details' });
      }
    });
  }

  onEwayFileSelected(event: any, row: EwayBillRow): void {
    const file: File = event?.target?.files?.[0];
    if (!file) return;

    row.uploading = true;
    this.uploadFile(file).subscribe({
      next: (url) => {
        row.uploading = false;
        row.docUrl = url;
      },
      error: () => {
        row.uploading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to upload e-Way bill document' });
      }
    });
  }

  hasNewLrRows(): boolean {
    return this.lrRows.some(x => !x.id);
  }

  hasNewEwayRows(): boolean {
    return this.ewayRows.some(x => !x.id);
  }

  // Single Save for the whole E-Way Bill section — every row must
  // have been fetched first, then all rows save in parallel.
  saveAllEway(): void {
    const newRows = this.ewayRows.filter(x => !x.id);
    if (!newRows.length) {
      this.messageService.add({
        severity: 'info',
        summary: 'No Changes',
        detail: 'There are no new E-Way Bills to save.'
      });
      return;
    }
    const notFetched = newRows.find(r => !r.fetched);
    if (notFetched) {
      this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Fetch e-Way bill details for every row before saving' });
      return;
    }

    const requests = newRows.map(row => {
      const payload: any = {
        orderId:     this.order.id,
        tripId:      this.tripData?.id,
        accountId:   this.order.accountId || 1,
        ewbNo:       row.ewayBillNumber,
        lrNo:        row.lrNumber,
        lrDate:      row.rawLrDate,
        invoiceNo:   row.invoiceNo,
        invoiceDate: row.rawInvoiceDate,
        invValue:    row.invoiceValue,
        vehicleNo:   row.vehicleNo,
        ewbDate:     row.rawEwbDate,
        validUpto:   row.rawValidUpto,
        gstNo:       row.gstNumber,
        docUrl:      row.docUrl || undefined,
      };
      return this.orderService.saveEwayBill(payload);
    });

    this.ewaySaving = true;
    forkJoin(requests).subscribe({
      next: () => {
        this.ewaySaving = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'E-Way bill(s) saved successfully' });
      },
      error: (err: any) => {
        this.ewaySaving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to save e-Way bill(s)' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // FILE UPLOAD
  // NOTE: form field name ('file') and the response shape used to
  // pull the URL back out are best-effort guesses — confirm against
  // the real utils-service contract and adjust if needed.
  // ─────────────────────────────────────────────

  uploadFile(file: File) {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post(this.uploadApi, formData).pipe(
      map((res: any) =>
        res?.responseObject?.url
        || res?.responseObject?.fileUrl
        || (typeof res?.responseObject === 'string' ? res.responseObject : '')
        || res?.url
        || res?.fileUrl
        || ''
      )
    );
  }

  // ─────────────────────────────────────────────
  // STATUS HELPERS
  // ─────────────────────────────────────────────

  statusLabel(): string {
    if (!this.order?.status) return '--';
    return this.order.status
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c: string) => c.toUpperCase());
  }

  statusBadgeClass(): string {
    const s = this.order?.status?.toUpperCase();
    if (!s) return 'badge-grey';
    if (s.includes('CONFIRMED') || s.includes('APPROVED')) return 'badge-green';
    if (s.includes('PENDING') || s.includes('GENERATED') || s.includes('INPROGRESS')) return 'badge-yellow';
    if (s.includes('REJECTED')) return 'badge-red';
    return 'badge-grey';
  }

  tripStatusLabel(): string {
    const tripStatus = this.order?.orderStatus?.find(s => s.statusType === 'TRIP_STATUS');
    if (!tripStatus) return '--';
    return this.humanize(tripStatus.status);
  }

  trackingStatus(): string {
    const tripStatus = this.order?.orderStatus?.find(s => s.statusType === 'TRIP_STATUS');
    return tripStatus?.status === 'TRIP_INPROGRESS' || tripStatus?.status === 'TRIP_STARTED'
      ? 'ACTIVE'
      : 'NOT STARTED';
  }

  humanize(value: string | undefined | null): string {
    if (!value) return '--';
    return value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase());
  }

  // ─────────────────────────────────────────────
  // COMPUTED
  // ─────────────────────────────────────────────

  balancePayable(row: LhcFormRow): number {
    const freight = this.order?.confirmedAmount || 0;
    const advance = row.advanceAmount || 0;
    return freight - advance;
  }

  formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined) return '--';
    return '₹' + Number(value).toLocaleString('en-IN');
  }

  onAdvanceAmountInput(value: number, row: any): void {
    const totalFreight = Number(this.order?.confirmedAmount || 0);
  
    if (!value) {
      row.advanceAmount = null;
      row.advancePercentage = null;
      return;
    }
  
    if (value > totalFreight) {
      // row.advanceAmount = totalFreight;
    } else {
      row.advanceAmount = value;
    }
  
    row.advancePercentage = Number(
      ((row.advanceAmount / totalFreight) * 100).toFixed(2)
    );
  }
  
  onAdvancePercentageChange(row: any): void {
    const totalFreight = Number(this.order?.confirmedAmount || 0);
  
    let percentage = Number(row.advancePercentage || 0);
  
    // Restrict between 0 and 100
    if (percentage < 0) {
      percentage = 0;
    }
  
    if (percentage > 100) {
      percentage = 100;
    }
  
    row.advancePercentage = percentage;
  
    row.advanceAmount = Number(
      ((totalFreight * percentage) / 100).toFixed(2)
    );
  }

  formatDisplayDate(d: Date | null | undefined): string {
    if (!d) return '--';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  // Parses "17-06-2026 04:17:00 +0000" → Date, for display formatting only.
  // The raw string itself (not this parsed Date) is what gets sent back
  // to the save API, since it's already in the exact format expected.
  parseApiDateString(raw: string | undefined): Date | null {
    if (!raw) return null;
    const match = raw.match(/(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2}):(\d{2})/);
    if (!match) return null;
    const [, dd, mm, yyyy, hh, min, ss] = match;
    return new Date(Date.UTC(+yyyy, +mm - 1, +dd, +hh, +min, +ss));
  }

  // Convert API date "29-06-2026 23:42:00 +0530" → "2026-06-29T23:42"
  toLocalDatetime(apiDate: string | undefined): string {
    if (!apiDate) return '';
    const match = apiDate.match(/(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2})/);
    if (!match) return '';
    const [, dd, mm, yyyy, hh, min] = match;
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  }

  // Convert "2026-06-29T23:42" → "29-06-2026 23:42:00 +0530"
  // Used by LHC, Loading Info, and LR — all on the tms-service/trip API.
  toApiDatetime(localDt: string): string {
    if (!localDt) return '';
    const [datePart, timePart] = localDt.split('T');
    const [yyyy, mm, dd] = datePart.split('-');
    return `${dd}-${mm}-${yyyy} ${timePart}:00 +0530`;
  }

  goBack(): void {
    this.router.navigate(['/orders']);
  }

  startTrip(): void {
    const payload: any = {
      id:         this.tripData?.id,
      orderId:    this.tripData?.orderId,
      accountId:  this.tripData?.accountId,
      customerId: this.tripData?.customerId,
      status:     'TRIP_INPROGRESS',
    };

    this.orderService.saveTrip(payload).subscribe({
      next: () => {
        this.getOrderTripById(this.orderId);
        this.getOrderDatabyId(this.orderId);
      },
      error: (err: any) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Failed to start trip' });
      }
    });
  }
}